import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type { ColorScheme }

export type RecordedFormResponsesSize = 'sm' | 'md'

const tokens = {
  dark: {
    textPrimary: 'text.primary',
    textSecondary: 'text.secondary',
    textTertiary: 'text.tertiary',
    textLink: 'text.link',
    borderSubtle: 'border.subtle',
  },
  light: {
    textPrimary: 'light.text.primary',
    textSecondary: 'light.text.secondary',
    textTertiary: 'light.text.tertiary',
    textLink: 'light.text.link',
    borderSubtle: 'light.border.subtle',
  },
} as const

const sizeMap = {
  sm: {
    rootGap: '12px',
    rootMt: '8px',
    gridGap: '8px 12px',
    gridColumns: '120px 1fr',
    gridMt: '8px',
    labelTextStyle: 'caption-medium',
    valueTextStyle: 'caption',
    linkTextStyle: 'caption',
    countTextStyle: 'small-caption',
    imageSize: '60px',
  },
  md: {
    rootGap: '16px',
    rootMt: '16px',
    gridGap: '12px 16px',
    gridColumns: '160px 1fr',
    gridMt: '12px',
    labelTextStyle: 'label',
    valueTextStyle: 'body',
    linkTextStyle: 'body',
    countTextStyle: 'caption',
    imageSize: '80px',
  },
} as const

export const rootStyles = (
  size: RecordedFormResponsesSize = 'md'
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: sizeMap[size].rootGap,
  mt: sizeMap[size].rootMt,
  // Anchors the absolutely-positioned expand/collapse toggle.
  position: 'relative',
})

// Wraps the sections so they can clip as one block while collapsed; the
// section gap moves here from the root so the toggle keeps its own spacing.
export const collapsibleContentStyles = (
  size: RecordedFormResponsesSize = 'md',
  isClipped = false,
  collapsedMaxHeight = 0
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: sizeMap[size].rootGap,
  ...(isClipped && {
    maxHeight: `${collapsedMaxHeight}px`,
    overflow: 'hidden',
  }),
})

export const seeMoreToggleStyles = (
  colorScheme: ColorScheme = 'dark',
  size: RecordedFormResponsesSize = 'md'
): SystemStyleObject => ({
  alignSelf: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  p: 0,
  color: tokens[colorScheme].textSecondary,
  textStyle: sizeMap[size].labelTextStyle,
  transition: 'color 0.15s ease',
  _hover: {
    color: tokens[colorScheme].textPrimary,
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor:
      colorScheme === 'light'
        ? 'light.interactive.focus-ring'
        : 'interactive.focus-ring',
    outlineOffset: '2px',
    borderRadius: '2px',
  },
})

export const expandToggleStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  display: 'inline-flex',
  alignItems: 'center',
  // Sits in the top-right corner, in line with the first section header.
  position: 'absolute',
  top: 0,
  right: 0,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  p: 0,
  color: tokens[colorScheme].textSecondary,
  transition: 'color 0.15s ease',
  _hover: {
    color: tokens[colorScheme].textPrimary,
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor:
      colorScheme === 'light'
        ? 'light.interactive.focus-ring'
        : 'interactive.focus-ring',
    outlineOffset: '2px',
    borderRadius: '2px',
  },
})

export const sectionHeaderStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  borderBottom: '1px solid',
  borderBottomColor: tokens[colorScheme].borderSubtle,
  color: tokens[colorScheme].textSecondary,
  pb: '8px',
  textStyle: 'overline',
})

export const answerGridStyles = (
  size: RecordedFormResponsesSize = 'md'
): SystemStyleObject => ({
  display: 'grid',
  gap: sizeMap[size].gridGap,
  gridTemplateColumns: sizeMap[size].gridColumns,
  mt: sizeMap[size].gridMt,
})

export const answerLabelStyles = (
  colorScheme: ColorScheme = 'dark',
  size: RecordedFormResponsesSize = 'md'
): SystemStyleObject => ({
  color: tokens[colorScheme].textPrimary,
  textStyle: sizeMap[size].labelTextStyle,
})

export const answerValueStyles = (
  colorScheme: ColorScheme = 'dark',
  size: RecordedFormResponsesSize = 'md'
): SystemStyleObject => ({
  color: tokens[colorScheme].textPrimary,
  textStyle: sizeMap[size].valueTextStyle,
  overflowWrap: 'break-word',
})

export const filePreviewContainerStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}

export const filePreviewGridStyles: SystemStyleObject = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
}

export const fileImageStyles = (
  size: RecordedFormResponsesSize = 'md'
): SystemStyleObject => ({
  borderRadius: '4px',
  boxShadow: 'sm',
  cursor: 'pointer',
  height: sizeMap[size].imageSize,
  objectFit: 'cover',
  opacity: 1,
  width: sizeMap[size].imageSize,
  _hover: { opacity: 0.8 },
})

export const fileLinkStyles = (
  colorScheme: ColorScheme = 'dark',
  size: RecordedFormResponsesSize = 'md'
): SystemStyleObject => ({
  color: tokens[colorScheme].textLink,
  textStyle: sizeMap[size].linkTextStyle,
  _hover: { textDecoration: 'underline' },
})

export const fileCountStyles = (
  colorScheme: ColorScheme = 'dark',
  size: RecordedFormResponsesSize = 'md'
): SystemStyleObject => ({
  color: tokens[colorScheme].textTertiary,
  textStyle: sizeMap[size].countTextStyle,
})
